import { action, computed, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import React, { useContext, useEffect } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../api/candidate";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { useCustomAlignDropdownOverlay } from "../../../Components/useCustomAlignOverlay";
import { getLocStore, useLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { Store2 } from "../../../WSUtils/Store";
import {
  invalidateQueries,
  useLatestQuery,
} from "../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../WSUtils/useStore";
import { WSFormGrid } from "../../../WSUtils/WSFormGrid";
import { sentryAssertData } from "../../../WSUtils/wsSentry";
import { CVEditContext, CVEditContextStore } from "../CVEditContext";
import {
  CVEditBaseField,
  cveField,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "../Fields/CVEditBaseField";
import { defaultCvEditDateFieldDef } from "../Fields/defaultCvEditDateField";
import { defaultCvEditTextFieldDef } from "../Fields/defaultCvEditTextField";

export const CVEditOtherDetailsSegment = () => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  const store = useStore2(
    "CVEditOtherDetailsSegmentStore",
    (dt) =>
      new CVEditOtherDetailsSegmentStore({
        name: "CVEditOtherDetailsSegmentStore",
        parentDt: dt,
        useHooks: (t: CVEditOtherDetailsSegmentStore) => {
          t.userInfo = useContext(UserInfoContext);
          t.queryClient = useQueryClient();
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  const { data: details } = useLatestQuery(
    ["candidateAPI.getDetailsForCandidate"],
    () =>
      candidateAPI.getDetailsForCandidate({
        userInfo,
      })
  );

  sentryAssertData([details], { userInfo });

  useEffect(
    action(() => {
      store.details = details;
    }),
    [details]
  );

  return (
    <Observer
      render={() => (
        <CVEditOtherDetailsSegmentStyles className="cve-other-details-segment cve-segment">
          <div className="cvp-h2 cve-header">
            {L.id("299600127c83").loc("Annet")}
          </div>
          <CVEditBaseField def={store.accountNumberField} />
          <CVEditBaseField def={store.ibanNrField} />
          <CVEditBaseField def={store.swiftkodeField} />
          <CVEditBaseField def={store.nationalIdentityNumberField} />
          <CVEditBaseField def={store.nextOfKinField} />
          <CVEditBaseField def={store.availableFromDateField} />
          <CVEditBaseField def={store.howDidYouFindUsField} />
        </CVEditOtherDetailsSegmentStyles>
      )}
    />
  );
};

const CVEditOtherDetailsSegmentStyles = styled(WSFormGrid)``;

export class CVEditOtherDetailsSegmentStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  @observable details: GetDetailsForCandidateResponse;

  getFieldDependencies = () => ({
    cvEditContext: this.cvEditContext,
    queryClient: this.queryClient,
    userInfo: this.userInfo,
    dt: this.dt,
  });

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @observable howDidYouFindUsField = cveField((getValueVersioner) => {
    const _this = this;
    const store = cveFieldStore({
      value: 0,
      get validationError(): string {
        return "";
      },
    });

    const valueVersioner = getValueVersioner(
      store,
      0,
      true,
      "howDidYouFindUsField"
    );

    this.dt.disposers.push(
      valueVersioner.onNewValue(() => {
        const data = this.details;
        if (!data?.ok) return;

        return {
          valueVersion: data?.valueVersion,
          getVal: () => data.typeFunnetgjennom,
        };
      })
    );

    const onChange: DropdownProps["onChange"] = action((evt) => {
      const newValue: number = evt.value;

      const mutate = () =>
        this.userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          userInfo: this.userInfo,
          fields: [
            {
              fieldName: "funnetgjennom",
              fieldValue: newValue.toString(),
            },
          ],
        });

      const invalidate = () =>
        invalidateQueries(_this.queryClient, [
          "candidateAPI.getDetailsForCandidate",
        ]);

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });

    return {
      store,
      id: "HowDidYouFindUs",
      getLabel: () => this.L.id("ecca26b00822").loc("Hvordan fant du oss"),
      hasUnsavedChanges: () => false,
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const userInfo = useContext(UserInfoContext);

        const { data: options } = useLatestQuery(
          ["typesAPI.getTypes(funnetgjennom)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo: this.userInfo,
              group: "funnetgjennom",
            })
        );

        sentryAssertData(options, { userInfo });

        const ref = useCustomAlignDropdownOverlay();

        return (
          <Dropdown
            ref={ref}
            inputId={def.id}
            options={options?.mapped}
            value={store.value}
            onChange={onChange}
          />
        );
      }),
    };
  });

  @observable availableFromDateField = cveField((getValueVersioner) => {
    const def = defaultCvEditDateFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "tilgjengeligFra",
      id: "AvailableFromDate",
      label: "Tilgjengelig fra",
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable nextOfKinField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "paarorende",
      id: "NextOfKin",
      label: "Pårørende",
      maxLength: 255,
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable nationalIdentityNumberField = cveField((getValueVersioner) => {
    const userInfo = this.userInfo;
    const fieldName = "personnummer";

    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName,
      id: "NationalIdentityNumber",
      label: "Personnummer",
      maxLength: 20,
      ...this.getFieldDependencies(),
      mutate: (newValue: string) =>
        userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          mapSentryData: (data) => {
            if (
              data.message.includes("Unable to update fields: personnummer")
            ) {
              return {
                ...data,
                originalMessage: data.message,
                message: this.L.id("2e342ed14210").loc(
                  "Kontakt byrå for endring av personnummer"
                ),
              };
            }

            return data;
          },
          userInfo: userInfo,
          fields: [
            {
              fieldName: fieldName,
              fieldValue: newValue,
            },
          ],
        }),
    });

    return def;
  });

  @observable swiftkodeField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "swiftkode",
      id: "SwiftCode",
      label: "SWIFT-kode",
      maxLength: 30,
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable ibanNrField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "ibannr",
      id: "IbanNumber",
      label: "IBAN-nummer",
      maxLength: 30,
      ...this.getFieldDependencies(),
    });

    return def;
  });

  @observable accountNumberField = cveField((getValueVersioner) => {
    const userInfo = this.userInfo;
    const fieldName = "bankkonto";

    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName,
      id: "AccountNumber",
      label: "Kontonummer",
      maxLength: 20,
      ...this.getFieldDependencies(),
      mutate: (newValue: string) =>
        userInfo.fullAPI.candidateAPI.updateCandidate({
          showSentryDialogOnError: true,
          mapSentryData: (data) => {
            if (data.message.includes("Unable to update fields: bankkonto")) {
              return {
                ...data,
                originalMessage: data.message,
                message: this.L.id("2e342ed14210").loc(
                  "Kontakt byrå for endring av bankkonto"
                ),
              };
            }

            return data;
          },
          userInfo: userInfo,
          fields: [
            {
              fieldName: fieldName,
              fieldValue: newValue,
            },
          ],
        }),
    });

    return def;
  });
}
